// Gestão DNA · Responsáveis — buscar, cadastrar, editar e vincular/desvincular alunos.
// Usa o MESMO cadastro canônico do DNACore (/api/guardians · /api/responsibles). Zero financeiro.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Loader2, Mail, MapPin, Phone, Plus, Search, Trash2, UserPlus, Users, X, Link2,
} from 'lucide-react';
import api from '@/lib/api';
import { usePermV2 } from '@/lib/perm_v2';

const CAMPOS = [
  { k: 'name', label: 'Nome completo', span: 2 },
  { k: 'cpf', label: 'CPF' },
  { k: 'relation_type', label: 'Parentesco' },
  { k: 'email', label: 'E-mail', span: 2 },
  { k: 'cellphone', label: 'Celular' },
  { k: 'phone', label: 'Telefone' },
  { k: 'zip_code', label: 'CEP' },
  { k: 'address', label: 'Endereço' },
  { k: 'address_number', label: 'Número' },
  { k: 'neighborhood', label: 'Bairro' },
  { k: 'city', label: 'Cidade' },
  { k: 'state', label: 'UF' },
];

export default function GestaoResponsaveis() {
  const { can, perms } = usePermV2();
  const isAdmin = perms?.is_admin_bypass === true;
  const podeCriar = isAdmin || can('responsaveis', 'criar');
  const podeEditar = isAdmin || can('responsaveis', 'editar');

  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);           // null = fechado
  const [saving, setSaving] = useState(false);
  const [vinculo, setVinculo] = useState(null);     // responsável em edição de vínculos
  const [alunoQ, setAlunoQ] = useState('');
  const [alunos, setAlunos] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const term = q.trim();
      if (term.length >= 2) {
        const { data } = await api.get('/guardians/search', { params: { q: term, limit: 50 } });
        setItems(data.items || []);
      } else {
        const { data } = await api.get('/responsibles');
        setItems((data || []).slice(0, 60));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar responsáveis');
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 280); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (params.get('novo') && podeCriar) { setForm({}); setParams({}, { replace: true }); }
  }, [params, podeCriar, setParams]);

  const salvar = async () => {
    if (!(form?.name || '').trim()) { toast.error('Informe o nome do responsável.'); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/guardians/${form.id}`, form);
        toast.success('Responsável atualizado');
      } else {
        await api.post('/guardians', { ...form, link_existing: true });
        toast.success('Responsável cadastrado');
      }
      setForm(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Erro ao salvar'));
    } finally { setSaving(false); }
  };

  const buscarAlunos = async (term) => {
    setAlunoQ(term);
    if (term.trim().length < 2) { setAlunos([]); return; }
    try {
      const { data } = await api.get('/v2/aluno/search', { params: { q: term, limit: 8 } });
      setAlunos(Array.isArray(data) ? data : (data?.items || []));
    } catch (_) { setAlunos([]); }
  };

  const vincular = async (studentId) => {
    try {
      await api.post(`/guardians/student/${studentId}/link`, {
        responsible_id: vinculo.id, relation: vinculo.relation_type || 'Responsável',
      });
      toast.success('Aluno vinculado');
      setAlunoQ(''); setAlunos([]); load();
      const { data } = await api.get('/guardians/search', { params: { q: vinculo.name, limit: 5 } });
      const atual = (data.items || []).find((i) => i.id === vinculo.id);
      if (atual) setVinculo(atual);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao vincular'); }
  };

  const desvincular = async (studentId) => {
    try {
      await api.delete(`/guardians/student/${studentId}/link/${vinculo.id}`);
      toast.success('Aluno desvinculado (cadastro preservado)');
      setVinculo((v) => ({ ...v, children: (v.children || []).filter((c) => c.id !== studentId) }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao desvincular'); }
  };

  return (
    <div className="mx-auto max-w-[1280px] space-y-6" data-testid="gestao-responsaveis">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-violet-600">Cadastro da família</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Responsáveis</h1>
          <p className="mt-2 max-w-xl text-base text-slate-500">
            Contatos, endereço e vínculo com os alunos. Informações financeiras não são exibidas neste portal.
          </p>
        </div>
        {podeCriar && (
          <button onClick={() => setForm({})}
            className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition-all hover:-translate-y-0.5 hover:bg-violet-500"
            data-testid="resp-novo">
            <UserPlus className="h-4 w-4" /> Novo responsável
          </button>
        )}
      </header>

      <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-sm focus-within:border-violet-300 focus-within:ring-4 focus-within:ring-violet-100">
        <Search className="h-4 w-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, CPF, e-mail ou telefone..."
          className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
          data-testid="resp-busca" />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-violet-500" />}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="resp-lista">
        {items.map((r, i) => (
          <motion.div key={r.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: Math.min(i, 8) * 0.03 }}
            className="rounded-3xl border border-slate-200/80 bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-26px_rgba(76,29,149,.4)]"
            data-testid={`resp-card-${r.id}`}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-xs font-bold text-emerald-700">
                {(r.name || '?').slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-bold text-slate-900">{r.name}</h3>
                <p className="text-xs text-slate-400">{r.relation_type || 'Responsável'}</p>
              </div>
            </div>
            <div className="mt-4 space-y-1.5 text-xs text-slate-600">
              {(r.cellphone || r.phone) && (
                <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" />{r.cellphone || r.phone}</div>
              )}
              {r.email && <div className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 text-slate-400" />{r.email}</div>}
              {(r.address || r.city) && (
                <div className="flex items-center gap-2 truncate"><MapPin className="h-3.5 w-3.5 text-slate-400" />{[r.address, r.city].filter(Boolean).join(', ')}</div>
              )}
            </div>
            {(r.children || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {r.children.map((c) => (
                  <span key={c.id} className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">{c.name}</span>
                ))}
              </div>
            )}
            <div className="mt-4 flex gap-2">
              {podeEditar && (
                <button onClick={() => setForm(r)}
                  className="flex-1 rounded-xl border border-slate-200 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-violet-300 hover:text-violet-700"
                  data-testid={`resp-editar-${r.id}`}>Editar</button>
              )}
              {podeEditar && (
                <button onClick={() => { setVinculo(r); setAlunoQ(''); setAlunos([]); }}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-violet-300 hover:text-violet-700"
                  data-testid={`resp-vinculos-${r.id}`}><Link2 className="h-3.5 w-3.5" /> Vínculos</button>
              )}
            </div>
          </motion.div>
        ))}
        {!loading && items.length === 0 && (
          <p className="col-span-full rounded-3xl border border-dashed border-slate-200 py-14 text-center text-sm text-slate-400">
            Nenhum responsável encontrado.
          </p>
        )}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={() => setForm(null)} data-testid="resp-form">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="max-h-[88vh] w-full max-w-2xl overflow-auto rounded-3xl bg-white p-7 shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold tracking-tight text-slate-900">
                  {form.id ? 'Editar responsável' : 'Novo responsável'}
                </h2>
                <p className="text-sm text-slate-500">Dados cadastrais e de contato — sem campos financeiros.</p>
              </div>
              <button onClick={() => setForm(null)} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"
                data-testid="resp-form-fechar"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {CAMPOS.map((c) => (
                <label key={c.k} className={c.span === 2 ? 'sm:col-span-2' : ''}>
                  <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">{c.label}</span>
                  <input value={form[c.k] || ''} onChange={(e) => setForm((f) => ({ ...f, [c.k]: e.target.value }))}
                    className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 outline-none transition-all focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
                    data-testid={`resp-campo-${c.k}`} />
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setForm(null)}
                className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50">Cancelar</button>
              <button onClick={salvar} disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-bold text-white shadow-lg shadow-violet-600/20 transition-all hover:bg-violet-500 disabled:opacity-50"
                data-testid="resp-salvar">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Salvar
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {vinculo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
          onClick={() => setVinculo(null)} data-testid="resp-vinculo-modal">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-bold tracking-tight text-slate-900">Vínculos de {vinculo.name}</h2>
                <p className="text-sm text-slate-500">Desvincular mantém o responsável no cadastro.</p>
              </div>
              <button onClick={() => setVinculo(null)} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {(vinculo.children || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3"
                  data-testid={`resp-vinculo-${c.id}`}>
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Users className="h-4 w-4 text-violet-500" />{c.name}
                  </span>
                  <button onClick={() => desvincular(c.id)} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"
                    data-testid={`resp-desvincular-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              {(vinculo.children || []).length === 0 && (
                <p className="py-4 text-center text-sm text-slate-400">Nenhum aluno vinculado.</p>
              )}
            </div>
            <div className="mt-5 border-t border-slate-100 pt-5">
              <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-400">Vincular aluno</span>
              <input value={alunoQ} onChange={(e) => buscarAlunos(e.target.value)}
                placeholder="Buscar aluno por nome..."
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-violet-300 focus:ring-4 focus:ring-violet-100"
                data-testid="resp-vincular-busca" />
              <div className="mt-2 space-y-1">
                {alunos.map((a) => (
                  <button key={a.id} onClick={() => vincular(a.id)}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm hover:bg-violet-50"
                    data-testid={`resp-vincular-${a.id}`}>
                    <span className="font-semibold text-slate-700">{a.name}</span>
                    <Plus className="h-4 w-4 text-violet-500" />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
